<template>
  <div
    class="base-payment-card flex flex-col items-center justify-center "
    :class="currentStep === PAST_DUE_STEPS.SUCCESS ? 'success' : 'fail'"
  >
    <div class="p-3 pt-4 space-y-3">
      <div class="w-max mx-auto">
        <div
          v-if="currentStep === PAST_DUE_STEPS.SUCCESS"
          class="rounded-full border-2 border-white w-5 h-5 flex items-center justify-center"
        >
          <NewCheckmarkIcon
            class="text-white w-full h-full"
          />
        </div>
        <WarningIcon
          v-else
          :width="20"
          :height="20"
          class="text-white"
        />
      </div>
      <div class="text-center">
        <!--  -->
        <BaseText
          type="label"
          size="sm"
          class="text-white"
        >
          {{ header }}
        </BaseText>
        <BaseText
          class="text-neutral-alpha-650"
          size="sm"
        >
          <span v-if="currentStep === PAST_DUE_STEPS.RETRY">
            Your recent payment on <span class="font-semibold text-white">{{ formattedStartDate }}</span> failed.
          </span>
          <span v-else-if="currentStep === PAST_DUE_STEPS.NON_ADMIN_CTA">
            You do not have permission to update payment details.
          </span>
          <span v-else>
            You can now continue
            using Foreplay <span class="text-white">🫡</span>
          </span>
          <div
            v-if="currentStep !== PAST_DUE_STEPS.SUCCESS"
            class="mt-4"
          >
            <span v-if="currentStep === PAST_DUE_STEPS.RETRY">
              Please try again{{ getUserSubscription.isAdmin ? ' or update your payment method.' : '.' }}
            </span>
            <span v-else>
              Please contact <span class="font-semibold text-white">{{ teamOwnerEmail }}</span> to regain access.
            </span>
          </div>
        </BaseText>
      </div>
    </div>
    <!-- Action Buttons -->
    <div
      v-if="currentStep !== PAST_DUE_STEPS.NON_ADMIN_CTA"
      class="space-y-1 w-full"
    >
      <button
        class="px-3 py-2 rounded-md w-full flex items-center justify-center"
        style="background: #0000003D;"
        :disabled="loading.retry"
        @click="handlePaymentAction"
      >
        <BaseLoadingSpinnerCircle
          v-if="loading.retry"
          class="text-white"
          :width="20"
          :height="20"
        />
        <div
          v-else-if="currentStep === PAST_DUE_STEPS.RETRY"
          class="flex items-center gap-1.5 justify-center"
        >
          <RefreshIcon
            :width="20"
            :height="20"
            class="text-white"
          />
          <BaseText
            type="label"
            size="sm"
            class="text-white"
          >
            Retry Payment
          </BaseText>
          <div class="p-0.5 flex items-center gap-1">
            <!--  -->
            <img
              v-if="defaultCardPreview.brandImg"
              :src="defaultCardPreview.brandImg"
              class="w-4 h-4"
            >
            <BaseText
              size="xs"
              style="color: #FFFFFFC2"
            >
              {{ defaultCardPreview.last4 }}
            </BaseText>
          </div>
        </div>
        <div v-else-if="currentStep === PAST_DUE_STEPS.SUCCESS">
          <BaseText
            type="label"
            size="sm"
            class="text-white"
          >
            Close
          </BaseText>
        </div>
      </button>
      <button
        v-if="getUserSubscription.isAdmin && currentStep !== PAST_DUE_STEPS.SUCCESS"
        class="px-3 py-2 rounded-md w-full flex items-center justify-center gap-1.5 hover:bg-neutral-alpha-10 transtion-colors"
        @click="openBillingSettings"
      >
        <SettingsUserIcon
          :width="20"
          :height="20"
          class="text-neutral-alpha-650"
        />
        <BaseText
          type="label"
          size="sm"
          class="text-white"
        >
          Billing Settings
        </BaseText>
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import API from '@/api'
import FirebaseAPI from '@/api/firebase'

import { SUBSCRIPTION_STATES } from '@/utils/stripe/productMeta'

// Icons
import WarningIcon from '@/components/globals/Icons/WarningIcon.vue'
import RefreshIcon from '@/components/globals/Icons/LensIcons/RefreshIcon.vue'
import SettingsUserIcon from '@/components/globals/Icons/SettingsIcons/SettingsIcon.vue'
import NewCheckmarkIcon from '../../globals/Icons/NewCheckmarkIcon.vue'
export default {
  name: 'PaymentPastDueCard',
  components: {
    WarningIcon,
    RefreshIcon,
    SettingsUserIcon,
    NewCheckmarkIcon
  },
  data () {
    const PAST_DUE_STEPS = {
      RETRY: 'retry',
      NON_ADMIN_CTA: 'non_admin_cta',
      SUCCESS: 'success'
    }
    return {
      PAST_DUE_STEPS,
      // Statics
      cardBrandAsset: {
        visa: {
          img: require('../../../assets/images/visa-dark.svg'),
          name: 'Visa'
        },
        mastercard: {
          img: require('../../../assets/images/mastercard.svg'),
          name: 'Mastercard'
        },
        amex: {
          img: require('../../../assets/images/americanexpress.svg'),
          name: 'American Express'
        }
      },

      currentStep: PAST_DUE_STEPS.RETRY,
      teamOwnerEmail: '',
      loading: {
        retry: false
      }
    }
  },
  computed: {
    ...mapGetters('AuthModule', ['getUserSubscription', 'getStripeCustomer', 'getTeam']),
    header () {
      if (this.currentStep === this.PAST_DUE_STEPS.NON_ADMIN_CTA) {
        return 'Contact Account Admin'
      } else if (this.currentStep === this.PAST_DUE_STEPS.SUCCESS) {
        return 'Payment Successful'
      }

      return 'Payment Failed'
    },
    defaultCardPreview () {
      const cardDetails = this.cardBrandAsset?.[this.getUserSubscription.paymentMethod?.brand]
      return {
        brandImg: cardDetails?.img,
        last4: this.getUserSubscription.paymentMethod?.last4
      }
    },
    formattedStartDate () {
      const startDateUnix = this.getUserSubscription.currentPeriodStart
      const startDate = new Date(startDateUnix * 1000)
      return startDate.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' })
    }
  },
  async mounted () {
    if (this.getTeam) {
      this.teamOwnerEmail = await this.getTeamOwnerEmail()
    }
  },
  methods: {
    ...mapMutations('AuthModule', ['SET_USER_SUBSCRIPTION']),
    ...mapMutations('MiscModule', ['SET_SETTINGS_DRAWER_VIEW_MODE']),
    handlePaymentAction () {
      if (this.currentStep === this.PAST_DUE_STEPS.SUCCESS) {
        this.SET_USER_SUBSCRIPTION({ ...this.getUserSubscription, status: SUBSCRIPTION_STATES.ACTIVE })
        this.$emit('close')
      } else {
        this.retryWithDefault()
      }
    },
    async getTeamOwnerEmail () {
      const team = this.getTeam
      const owner = await FirebaseAPI.Users.getById(team.created_by)

      return owner.email
    },
    openBillingSettings () {
      this.SET_SETTINGS_DRAWER_VIEW_MODE('billing')
    },
    async retryWithDefault () {
      this.loading.retry = true
      try {
        // Retry the past due charge
        await API.Stripe.retryPastDuePayment(
          this.getStripeCustomer.stripeId
        )

        this.currentStep = this.PAST_DUE_STEPS.SUCCESS
      } catch (e) {
        this.$showAlert({
          message: 'Payment failed, please try adding a new card.',
          type: 'error'
        })

        const now = new Date().getTime()
        const fiveMinutesLater = now + 5 * 60 * 1000
        const item = {
          expiry: fiveMinutesLater
        }
        localStorage.setItem('last-retry', JSON.stringify(item))
        if (this.getUserSubscription.isAdmin) {
          this.openBillingSettings()
        } else {
          this.currentStep = this.PAST_DUE_STEPS.NON_ADMIN_CTA
        }
      }
      this.loading.retry = false
    }
  }
}
</script>

<style scoped>
.base-payment-card {
  border-radius: 12px;
  padding: 4px;
  backdrop-filter: blur(40px);
}

.base-payment-card.fail {
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.12) 0%, rgba(244, 0, 58, 0.12) 100%), linear-gradient(0deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.12) 100%), rgba(15, 16, 25, 0.9);
}

.base-payment-card.success{
background: linear-gradient(180deg, rgba(255, 255, 255, 0.12) 0%, rgba(0, 199, 169, 0.12) 100%), linear-gradient(0deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.12) 100%), rgba(15, 16, 25, 0.68);
}
</style>
